import {
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { searchTransfers } from '../../api/business/endpoints';
import type { TransferDirection, TransferSortKey, TransferStatus } from '../../api/business/types';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { formatBytes, formatDateTime } from '../../components/format';
import { ServerPagination } from '../../components/ServerPagination';
import { SortableCell } from '../../components/SortableCell';
import { TableFilters } from '../../components/TableFilters';
import { TableSkeletonRows } from '../../components/TableSkeletonRows';
import { isRunning } from '../../components/transfers';
import { useSort } from '../../components/useSort';
import { useLanguage } from '../../i18n/LanguageContext';

import { TransferStatusChip } from './TransferStatusChip';

const DIRECTIONS: Array<TransferDirection> = ['EXPORT', 'IMPORT'];
const STATUSES: Array<TransferStatus> = [
  'AWAITING_UPLOAD',
  'QUEUED',
  'RUNNING',
  'VALIDATING',
  'READY',
  'APPLYING',
  'DONE',
  'FAILED',
  'EXPIRED',
];

/** The company's past and running transfers (R0037), newest first; a row opens the transfer above. */
export const TransferHistoryTable = ({
  companyId,
  selected,
  onSelect,
}: {
  companyId: string;
  selected: string | null;
  onSelect: (transferId: string) => void;
}) => {
  const { language, t } = useLanguage();
  const [direction, setDirection] = useState<TransferDirection | ''>('');
  const [status, setStatus] = useState<TransferStatus | ''>('');
  const [page, setPage] = useState(0);
  const sort = useSort<TransferSortKey>('createdAt', 'desc', () => setPage(0));
  const query = useQuery({
    queryKey: ['transfers', companyId, direction, status, page, sort.key, sort.direction],
    queryFn: () =>
      searchTransfers(companyId, {
        direction: direction || undefined,
        status: status || undefined,
        page: page + 1,
        sort: sort.key,
        order: sort.direction,
      }),
    placeholderData: keepPreviousData,
    refetchInterval: (current) =>
      (current.state.data?.items ?? []).some((transfer) => isRunning(transfer.status)) ? 3000 : false,
  });
  const rows = query.data?.items ?? [];

  return (
    <Paper variant="outlined">
      <ErrorNotice error={query.error} onRetry={() => void query.refetch()} />
      <TableFilters>
        <TextField
          select
          size="small"
          label={t('transfers.direction')}
          value={direction}
          onChange={(event) => {
            setDirection(event.target.value as TransferDirection | '');
            setPage(0);
          }}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">{t('common.all')}</MenuItem>
          {DIRECTIONS.map((one) => (
            <MenuItem key={one} value={one}>
              {t(`transferDirection.${one}` as const)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label={t('transfers.status')}
          value={status}
          onChange={(event) => {
            setStatus(event.target.value as TransferStatus | '');
            setPage(0);
          }}
          sx={{ minWidth: 190 }}
        >
          <MenuItem value="">{t('common.all')}</MenuItem>
          {STATUSES.map((one) => (
            <MenuItem key={one} value={one}>
              {t(`transferStatus.${one}` as const)}
            </MenuItem>
          ))}
        </TextField>
      </TableFilters>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <SortableCell sort={sort} column="createdAt">
                {t('transfers.createdAt')}
              </SortableCell>
              <SortableCell sort={sort} column="direction">
                {t('transfers.direction')}
              </SortableCell>
              <TableCell>{t('transfers.what')}</TableCell>
              <SortableCell sort={sort} column="status">
                {t('transfers.status')}
              </SortableCell>
              <SortableCell sort={sort} column="sizeBytes" align="right">
                {t('transfers.size')}
              </SortableCell>
              <SortableCell sort={sort} column="finishedAt">
                {t('transfers.finishedAt')}
              </SortableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {query.isLoading ? (
              <TableSkeletonRows columns={6} />
            ) : (
              rows.map((transfer) => (
                <TableRow
                  key={transfer.id}
                  hover
                  selected={transfer.id === selected}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => onSelect(transfer.id)}
                >
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {formatDateTime(transfer.createdAt, language)}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {transfer.requestedByEmail}
                    </Typography>
                  </TableCell>
                  <TableCell>{t(`transferDirection.${transfer.direction}` as const)}</TableCell>
                  <TableCell>
                    {transfer.direction === 'EXPORT'
                      ? (transfer.scope?.categoryName ?? t('transfers.scopeAll'))
                      : (transfer.fileName ?? t('common.none'))}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {transfer.direction === 'EXPORT'
                        ? t(transfer.includeMedia ? 'transfers.withMedia' : 'transfers.withoutMedia')
                        : transfer.mode !== null
                          ? `${t(`transferMode.${transfer.mode}` as const)} · ${t(`transferStock.${transfer.stockMode ?? 'IGNORE'}` as const)}`
                          : ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <TransferStatusChip status={transfer.status} />
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {transfer.sizeBytes === null
                      ? t('common.none')
                      : formatBytes(transfer.sizeBytes, language)}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {transfer.finishedAt === null
                      ? t('common.none')
                      : formatDateTime(transfer.finishedAt, language)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {!query.isLoading && rows.length === 0 ? (
        <EmptyState title={t('transfers.noHistory')} detail={t('transfers.noHistoryDetail')} />
      ) : null}
      <ServerPagination
        page={page}
        pageSize={query.data?.pageSize ?? 25}
        total={query.data?.total ?? 0}
        onPageChange={setPage}
      />
    </Paper>
  );
};
